import React, { useEffect, useState } from "react";
import {
  clearStoredClinicAdminClinicId,
  clearStoredPortalRoleChoice,
  distinctSelectableAssignments,
  findStoredAssignment,
  getStoredPortalAssignment,
  isOhtScopedRoleName,
  normRoleName as storedRoleName,
  parsePositiveInt,
  persistPortalAssignment,
} from "../../utils/portalRoleChoice";

type AuthMeResponse = {
  clientPrincipal: null | {
    userDetails: string;
    identityProvider: string;
  };
};

type BootstrapRole = {
  roleName: string;
  clinicId?: string | number | null;
  clinicName?: string | null;
  regionId?: string | null;
  ohtId?: string | number | null;
  ohtName?: string | null;
  ohtCode?: string | null;
  familyDoctorId?: string | null;
  specialistId?: string | null;
  isActive?: boolean | number | null;
};

type BootstrapResponse = {
  userId: number;
  status: "Pending" | "Approved" | "Disabled" | string;
  roles?: BootstrapRole[];
  primaryRole?: string | null;
};

type MyStatusResponse = {
  hasRequest: boolean;
  latest: null | {
    accessRequestId: number;
    requestedRole: string;
    clinicId: number | null;
    regionId: number | null;
    status: string;
    submittedAt: string;
    reviewedAt?: string | null;
  };
};

const RETURN_TO_ONCE_KEY = "ocf.portal.returnTo.once"; // prevents loops

/** Normalize a role name to a stable key. */
function normRoleName(name: any): string {
  return String(name || "").trim().toLowerCase();
}

function isRoleActive(v: any): boolean {
  // If isActive is missing, treat as active (older payloads).
  if (v === undefined || v === null) return true;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v === 1;
  return String(v).toLowerCase() === "true";
}

function routeForRoleName(roleName: string): string | null {
  const r = normRoleName(roleName);
  if (!r) return null;

  // Locked v1: SuperAdmin has its own portal path
  if (r === "superadmin") return "/portal/super-admin";
  if (r === "ohtadmin") return "/portal/oht-admin";
  if (r === "communitynavigator") return "/portal/community-navigator";

  if (r === "clinicadmin") return "/portal/clinic-admin";
  if (r === "familydoctor") return "/portal/family-doctor";
  if (r === "specialist") return "/portal/specialist";
  if (r === "regionadmin") return "/portal/region-admin";

  // Keep placeholder admin portal for Admin/SystemAdmin (if present)
  if (r === "admin" || r === "systemadmin") return "/portal/admin";

  return null;
}

/**
 * Return the role required for a protected portal path.
 * This is used only for returnTo safety. Direct route protection is still
 * handled separately by each portal shell/API.
 */
function requiredRoleForPortalPath(path: string | null): string | null {
  const p = String(path || "").trim().toLowerCase();
  if (!p) return null;

  if (p === "/portal/super-admin" || p.startsWith("/portal/super-admin/")) {
    return "superadmin";
  }

  if (p === "/portal/oht-admin" || p.startsWith("/portal/oht-admin/")) {
    return "ohtadmin";
  }

  if (
    p === "/portal/community-navigator" ||
    p.startsWith("/portal/community-navigator/")
  ) {
    return "communitynavigator";
  }

  if (p === "/portal/clinic-admin" || p.startsWith("/portal/clinic-admin/")) {
    return "clinicadmin";
  }

  if (p === "/portal/family-doctor" || p.startsWith("/portal/family-doctor/")) {
    return "familydoctor";
  }

  if (p === "/portal/specialist" || p.startsWith("/portal/specialist/")) {
    return "specialist";
  }

  if (p === "/portal/region-admin" || p.startsWith("/portal/region-admin/")) {
    return "regionadmin";
  }

  if (p === "/portal/admin" || p.startsWith("/portal/admin/")) {
    return "admin";
  }

  return null;
}

function canHonorReturnToForRoles(
  returnTo: string | null,
  activeRoleNames: string[]
): boolean {
  if (!returnTo) return false;

  const required = requiredRoleForPortalPath(returnTo);

  // Non-role-specific internal portal paths remain safe to honor.
  if (!required) return true;

  // Admin/SystemAdmin compatibility.
  if (required === "admin") {
    return (
      activeRoleNames.includes("admin") || activeRoleNames.includes("systemadmin")
    );
  }

  return activeRoleNames.includes(required);
}

/** Deterministic precedence fallback (legacy). */
function pickPortalRoute(
  roles: BootstrapRole[] | undefined | null
): string | null {
  const list = Array.isArray(roles) ? roles : [];
  const active = list.filter((r) => isRoleActive(r?.isActive));

  const names = new Set(
    active.map((r) => normRoleName(r.roleName)).filter(Boolean)
  );

  // Precedence:
  // SuperAdmin > OHTAdmin > CommunityNavigator > ClinicAdmin > FamilyDoctor > Specialist > RegionAdmin > Admin/SystemAdmin
  if (names.has("superadmin")) return "/portal/super-admin";
  if (names.has("ohtadmin")) return "/portal/oht-admin";
  if (names.has("communitynavigator")) return "/portal/community-navigator";
  if (names.has("clinicadmin")) return "/portal/clinic-admin";
  if (names.has("familydoctor")) return "/portal/family-doctor";
  if (names.has("specialist")) return "/portal/specialist";
  if (names.has("regionadmin")) return "/portal/region-admin";
  if (names.has("admin") || names.has("systemadmin")) return "/portal/admin";

  return null;
}

function getActiveRoleNames(roles: BootstrapRole[] | undefined | null): string[] {
  const list = Array.isArray(roles) ? roles : [];
  const active = list.filter((r) => isRoleActive(r?.isActive));
  const uniq = new Set(
    active.map((r) => normRoleName(r.roleName)).filter(Boolean)
  );
  return Array.from(uniq);
}

function getActiveAssignments(
  roles: BootstrapRole[] | undefined | null
): BootstrapRole[] {
  const list = Array.isArray(roles) ? roles : [];
  return list.filter(
    (r) => isRoleActive(r?.isActive) && !!normRoleName(r.roleName)
  );
}

function findClinicAdminAssignment(
  assignments: BootstrapRole[],
  clinicId: number | null
): BootstrapRole | null {
  if (clinicId === null) return null;

  return (
    assignments.find(
      (r) =>
        normRoleName(r.roleName) === "clinicadmin" &&
        parsePositiveInt(r.clinicId) === clinicId
    ) || null
  );
}

function persistClinicAdminAssignment(role: BootstrapRole | null): boolean {
  return persistPortalAssignment(role);
}

function persistUnambiguousClinicAdminScope(assignments: BootstrapRole[]): boolean {
  const stored = getStoredPortalAssignment();
  const storedMatch = findClinicAdminAssignment(assignments, stored.clinicId);
  if (storedMatch) {
    return persistClinicAdminAssignment(storedMatch);
  }

  const clinicAdminAssignments = assignments.filter(
    (r) =>
      normRoleName(r.roleName) === "clinicadmin" &&
      parsePositiveInt(r.clinicId) !== null
  );

  if (clinicAdminAssignments.length === 1) {
    return persistClinicAdminAssignment(clinicAdminAssignments[0]);
  }

  return false;
}

/**
 * Only allow internal, same-app paths.
 * - must start with "/"
 * - must NOT start with "//"
 * - must NOT contain a scheme like "http:"
 */
function sanitizeReturnTo(value: string | null): string | null {
  const v = (value || "").trim();
  if (!v) return null;
  if (!v.startsWith("/")) return null;
  if (v.startsWith("//")) return null;
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(v)) return null; // blocks "http:", "javascript:", etc.
  return v;
}

function getReturnToFromUrl(): string | null {
  try {
    const sp = new URLSearchParams(window.location.search || "");
    const raw = sp.get("returnTo");
    return sanitizeReturnTo(raw);
  } catch {
    return null;
  }
}

function shouldHonorReturnToOnce(returnTo: string | null): boolean {
  if (!returnTo) return false;
  try {
    const last = window.sessionStorage.getItem(RETURN_TO_ONCE_KEY);
    // If we've already used this exact returnTo in this tab session, don't loop.
    return last !== returnTo;
  } catch {
    // If sessionStorage is blocked, still try to honor once (best effort).
    return true;
  }
}

function markReturnToUsed(returnTo: string | null) {
  if (!returnTo) return;
  try {
    window.sessionStorage.setItem(RETURN_TO_ONCE_KEY, returnTo);
  } catch {
    // ignore
  }
}

/**
 * Narrow provisioning exception for invited providers.
 *
 * Clinic Admins provision providers by invitation. A brand-new invitee is
 * typically Pending until POST /api/portal/provider-invites/accept upgrades
 * them. This helper therefore recognizes ONLY the invite acceptance route
 * so PortalHomePage can send the user there before the ordinary Pending
 * redirect. It does not grant roles, approve the user, or validate the token.
 *
 * Requires an already-sanitized internal path (sanitizeReturnTo). Lookalike
 * paths such as /portal/accept-invite-fake or /portal/accept-invite/other
 * are rejected.
 */
function isProviderInviteReturnTo(
  returnTo: string | null
): returnTo is string {
  const safe = sanitizeReturnTo(returnTo);
  if (!safe) return false;

  const pathname = safe.split(/[?#]/, 1)[0];
  return pathname === "/portal/accept-invite";
}

async function getMyAccessRequestStatus(): Promise<MyStatusResponse | null> {
  try {
    const res = await fetch("/api/access-requests/my-status", {
      credentials: "include",
    });
    if (!res.ok) return null;
    const json = (await res.json()) as MyStatusResponse;
    return json;
  } catch {
    return null;
  }
}

async function sleep(ms: number) {
  await new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchBootstrap(): Promise<BootstrapResponse> {
  const bootRes = await fetch("/api/portal-auth-bootstrap", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
  });

  if (!bootRes.ok) {
    const txt = await bootRes.text();
    throw new Error(`Bootstrap failed (${bootRes.status}): ${txt}`);
  }

  return (await bootRes.json()) as BootstrapResponse;
}

const PortalHomePage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setError(null);

        // Read returnTo once (invite flow, role landing, etc.)
        const returnTo = getReturnToFromUrl();

        const meRes = await fetch("/.auth/me", { credentials: "include" });
        const me = (await meRes.json()) as AuthMeResponse;

        if (!me.clientPrincipal) {
          // If we arrived here with a returnTo (e.g. invite), preserve it on login.
          if (returnTo) {
            window.location.href = `/portal/login?returnTo=${encodeURIComponent(
              returnTo
            )}`;
            return;
          }
          window.location.href = "/portal/login";
          return;
        }

        setEmail(me.clientPrincipal.userDetails || null);

        /**
         * Important ordering:
         * - Do NOT immediately redirect to returnTo.
         * - First run bootstrap so SWA auth + API sees principal and the user row is upserted.
         * - Block Disabled users before any returnTo (including invite acceptance).
         * - Allow ONLY the sanitized /portal/accept-invite route to run while Pending,
         *   because invite acceptance is what provisions the invited provider.
         * - Then apply ordinary Pending handling.
         * - Then check account status and active roles.
         * - Only then honor other returnTo paths if the user has the required portal role.
         */
        let boot = await fetchBootstrap();

        if (boot.status === "Disabled") {
          window.location.href = "/portal/access-disabled";
          return;
        }

        if (
          isProviderInviteReturnTo(returnTo) &&
          shouldHonorReturnToOnce(returnTo)
        ) {
          markReturnToUsed(returnTo);
          window.location.href = returnTo;
          return;
        }

        if (boot.status === "Pending") {
          window.location.href = "/portal/access-pending";
          return;
        }

        let activeRoleNames = getActiveRoleNames(boot.roles);
        let activeAssignments = getActiveAssignments(boot.roles);
        let selectableAssignments = distinctSelectableAssignments(boot.roles);

        // Edge case: Approved but roles not yet visible (propagation delay).
        // If latest request is Approved, retry bootstrap a few times before sending to /portal/no-access.
        if (activeRoleNames.length === 0) {
          const myReq = await getMyAccessRequestStatus();
          const latestStatus = myReq?.latest?.status || null;
          const latestNorm = normRoleName(latestStatus);

          if (latestNorm === "pending") {
            window.location.href = "/portal/access-pending";
            return;
          }

          if (latestNorm === "approved") {
            // Retry bootstrap 3 times with small backoff
            const delays = [400, 900, 1400];
            for (const d of delays) {
              await sleep(d);
              boot = await fetchBootstrap();
              activeRoleNames = getActiveRoleNames(boot.roles);
              activeAssignments = getActiveAssignments(boot.roles);
              selectableAssignments = distinctSelectableAssignments(boot.roles);
              if (activeRoleNames.length > 0) break;
            }
          }

          if (activeRoleNames.length === 0) {
            window.location.href = "/portal/no-access";
            return;
          }
        }

        // Honor returnTo only after role validation.
        // This prevents returnTo from bypassing role-aware routing into Super Admin or other protected portals.
        if (
          returnTo &&
          shouldHonorReturnToOnce(returnTo) &&
          canHonorReturnToForRoles(returnTo, activeRoleNames)
        ) {
          markReturnToUsed(returnTo);

          if (requiredRoleForPortalPath(returnTo) === "clinicadmin") {
            persistUnambiguousClinicAdminScope(activeAssignments);
          } else {
            clearStoredClinicAdminClinicId();
          }

          window.location.href = returnTo;
          return;
        }

        // Multiple selectable assignments need a choice unless the stored
        // assignment is still active. OHT restore matches role and OHT together.
        if (selectableAssignments.length > 1) {
          const stored = getStoredPortalAssignment();
          const storedRole = storedRoleName(stored.roleName);

          if (storedRole === "clinicadmin" || isOhtScopedRoleName(storedRole)) {
            const match = findStoredAssignment(selectableAssignments, stored);
            if (match && persistPortalAssignment(match)) {
              const storedRoute = routeForRoleName(storedRole);
              if (storedRoute) {
                window.location.href = storedRoute;
                return;
              }
            }

            clearStoredPortalRoleChoice();
            window.location.href = "/portal/select-role";
            return;
          }

          if (storedRole && activeRoleNames.includes(storedRole)) {
            const storedRoute = routeForRoleName(storedRole);
            if (storedRoute && persistPortalAssignment({ roleName: storedRole })) {
              window.location.href = storedRoute;
              return;
            }
          }

          if (storedRole && !activeRoleNames.includes(storedRole)) {
            clearStoredPortalRoleChoice();
          }

          window.location.href = "/portal/select-role";
          return;
        }

        if (selectableAssignments.length === 0) {
          clearStoredPortalRoleChoice();
          window.location.href = "/portal/select-role";
          return;
        }

        // Single selectable assignment routes directly and remembers its scope.
        const onlyAssignment = selectableAssignments[0];
        persistPortalAssignment(onlyAssignment);
        const route =
          routeForRoleName(onlyAssignment?.roleName || "") ||
          pickPortalRoute([onlyAssignment]);

        if (!route) {
          window.location.href = "/portal/no-access";
          return;
        }

        window.location.href = route;
      } catch (e: any) {
        setError(e?.message || "Failed to load portal.");
        setLoading(false);
      }
    };

    run();
  }, []);

  return (
    <div
      style={{
        padding: 24,
        fontFamily:
          "system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
      }}
    >
      <h1 style={{ margin: 0 }}>Portal</h1>
      {loading ? (
        <p style={{ marginTop: 12 }}>Loading… {email ? `(${email})` : ""}</p>
      ) : (
        <p style={{ marginTop: 12, color: "#b91c1c" }}>{error}</p>
      )}
    </div>
  );
};

export default PortalHomePage;